import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useLocale, t } from '../hooks/useT';

interface FriendScore {
  userId: string;
  displayName: string;
  score: number;
  won: boolean;
}

interface ArchiveItem {
  puzzleId: string;
  puzzleNumber: number;
  publishDate: string;
  status: 'not_played' | 'in_progress' | 'won' | 'lost';
  score: number | null;
  runId?: string;
  friendScores?: FriendScore[];
}

function formatArchiveDate(dateStr: string, locale: string) {
  const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return dateStr;
  return new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : 'fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Paris',
  }).format(new Date(Date.UTC(y, m - 1, d, 12, 0, 0)));
}

function statusLabel(item: ArchiveItem) {
  if (item.status === 'not_played') return t('archives.notPlayed');
  if (item.status === 'in_progress') return t('archives.inProgress');
  if (item.status === 'won') return `${t('archives.played')} — ${item.score ?? 0} pt`;
  return `${t('archives.played')} — ${item.score ?? 0} pt`;
}

export function ArchivesPage() {
  const { locale } = useLocale();
  const [items, setItems] = useState<ArchiveItem[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (nextOffset: number, append: boolean) => {
    const data = await api<{ items: ArchiveItem[]; hasMore: boolean }>(
      `/archives?limit=30&offset=${nextOffset}`,
    );
    setItems((prev) => (append ? [...prev, ...data.items] : data.items));
    setHasMore(data.hasMore);
    setOffset(nextOffset + data.items.length);
  }, []);

  useEffect(() => {
    load(0, false)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      await load(offset, true);
    } catch (e: unknown) {
      setError((e as Error).message);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <main className="page archives">
      <h1>{t('archives.title')}</h1>
      <p className="sub">{t('archives.sub')}</p>
      <p className="muted archives-hint">{t('archives.officialHint')}</p>

      {error && <p className="error" role="alert">{error}</p>}

      {loading ? (
        <p className="loading">{t('common.loading')}</p>
      ) : (
        <>
          <ul className="archives-list">
            {items.map((item) => (
              <li key={item.puzzleId} className="archives-item">
                <div className="archives-item-main">
                  <strong>#{item.puzzleNumber}</strong>
                  <span className="muted">{formatArchiveDate(item.publishDate, locale)}</span>
                  <span className={`archives-badge archives-badge--${item.status}`}>
                    {statusLabel(item)}
                  </span>
                </div>
                {item.friendScores && item.friendScores.length > 0 && (
                  <div className="archives-friend-scores">
                    <span className="archives-friend-scores-label">{t('archives.friendScores')}</span>
                    <ul className="archives-friend-scores-list">
                      {item.friendScores.map((f) => (
                        <li key={f.userId}>
                          <span className="archives-friend-name">{f.displayName}</span>
                          <span className={`archives-friend-pts${f.won ? '' : ' lost'}`}>
                            {f.score} pt
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="archives-item-actions">
                  {item.status === 'won' || item.status === 'lost' ? (
                    <Link
                      to="/result"
                      state={{ fromRun: item.runId }}
                      className="btn secondary admin-btn-sm"
                    >
                      {t('archives.viewResult')}
                    </Link>
                  ) : (
                    <Link
                      to={`/archives/${item.puzzleNumber}/play`}
                      className="btn primary admin-btn-sm"
                    >
                      {item.status === 'in_progress' ? t('archives.resume') : t('archives.play')}
                    </Link>
                  )}
                  <Link
                    to={`/archives/${item.puzzleNumber}/leaderboard`}
                    className="btn ghost admin-btn-sm"
                  >
                    {t('archives.leaderboard')}
                  </Link>
                </div>
              </li>
            ))}
          </ul>
          {!items.length && <p className="muted">{t('archives.empty')}</p>}
          {hasMore && (
            <button
              type="button"
              className="btn secondary block archives-load-more"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? t('common.loading') : t('archives.loadMore')}
            </button>
          )}
        </>
      )}

      <nav className="result-nav">
        <Link to="/play">{t('profile.playToday')}</Link>
        <Link to="/leaderboard">{t('leaderboard.title')}</Link>
        <Link to="/">{t('gallery.home')}</Link>
      </nav>
    </main>
  );
}
